import api from '@/api';
import { mapFromApi } from '@/mappers';
import type { AppUser, LoanApplication } from '@/types';
import { computed, ref } from 'vue';
import type { ApplicationDto } from '@/api/backend/users/applications';

class ConversionException extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConversionException';
  }
}

export function useScenarioApi() {
  const isProcessing = ref(0);
  const retrieveBrokerScenario = async (
    appId: string,
  ): Promise<{
    success: boolean;
    error?: string;
    value?: LoanApplication;
  }> => {
    return await retrieveScenario(appId, 'broker');
  };

  const retrieveCustomerScenario = async (
    appId: string,
  ): Promise<{
    success: boolean;
    error?: string;
    value?: LoanApplication;
  }> => {
    return await retrieveScenario(appId, 'customer');
  };

  const retrieveScenario = async (
    appId: string,
    type: 'broker' | 'customer',
  ): Promise<{
    success: boolean;
    error?: string;
    value?: LoanApplication;
  }> => {
    isProcessing.value++;
    try {
      const scenarioApi = api.backend.brokers.scenario;
      const response =
        type === 'broker'
          ? await scenarioApi.retrieveBrokerScenario({ id: appId })
          : await scenarioApi.retrieveCustomerScenario({ id: appId });
      const application = response.data.application as ApplicationDto;
      let appUser: AppUser = 'Broker';
      if (type === 'customer') {
        appUser = 'Primary'; // primary always represents customer as user
      }
      try {
        const expenseTypeResponse = await api.backend.public.listExpenseTypes(application.structuralVersionNumber);
        const expenseTypes = expenseTypeResponse.expenseTypes ?? [];
        const applicationProgress = mapFromApi.applicationProgress(application, expenseTypes, appUser);
        return { success: true, value: applicationProgress };
      } catch (e) {
        throw new ConversionException('Cannot convert payload into application object');
      }
    } catch (error) {
      if (error instanceof ConversionException) {
        return { success: false, error: 'conversion-fail' };
      } else {
        return { success: false, error: 'network-error' };
      }
    } finally {
      isProcessing.value--;
    }
  };

  return {
    isLoading: computed(() => isProcessing.value > 0),
    retrieveBrokerScenario,
    retrieveCustomerScenario,
  };
}
